import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

/**
 * Search-intent sections.
 *
 * Each entry answers the exact phrases Phoenix homeowners and property managers
 * type into Google for that page's service, in plain language, with a contextual
 * link to the next logical page. Keyed by page slug (no leading slash).
 */
export type TermSection = { heading: string; body: ReactNode };

const A = "font-semibold text-primary underline underline-offset-2 hover:text-accent";

export const SEARCH_TERM_SECTIONS: Record<string, TermSection[]> = {
  "foam-roofing": [
    {
      heading: "Spray foam roofing, SPF roofing, polyurethane foam roofing — what's the difference?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            These are all the same roof. <strong>Spray foam roofing</strong> is the everyday name,{" "}
            <strong>SPF roofing</strong> is the trade abbreviation (spray polyurethane foam), and{" "}
            <strong>polyurethane foam roofing</strong> is the chemistry. When a Phoenix contractor
            quotes you a &ldquo;foam roof,&rdquo; they mean closed-cell polyurethane foam sprayed on
            your deck and sealed with a reflective elastomeric coating.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Foam roof / foam roofing:</strong> the finished system — foam plus coating,
              installed as one seamless surface with no laps or fasteners.
            </li>
            <li>
              <strong>SPF roofing:</strong> what you'll see on specs, warranties, and manufacturer
              data sheets. Same product, technical wording.
            </li>
            <li>
              <strong>Polyurethane foam roofing:</strong> the material class. Ours is two-part,
              closed-cell, roofing-grade foam — not attic insulation foam.
            </li>
            <li>
              <strong>Foam roof coating:</strong> only the topcoat, renewed about every 5 years. See{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>foam roof coating and recoating</Link>.
            </li>
          </ul>
          <p>
            If your existing foam roof is leaking or blistering rather than due for replacement,
            start at <Link to="/foam-roof-repair-phoenix" className={A}>foam roof repair in Phoenix</Link>.
            For a commercial building, see{" "}
            <Link to="/commercial-foam-roofing" className={A}>commercial foam roofing</Link>.
          </p>
        </div>
      ),
    },
    {
      heading: "How spray foam roofing is applied, step by step",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Most people have never watched a foam roof go on, so here's exactly what happens on
            your house — start to finish, usually in one to three days on a typical Phoenix home.
          </p>
          <ol className="list-decimal space-y-3 pl-5">
            <li>
              <strong>Day one is cleaning, not spraying.</strong> The crew pressure-cleans the
              deck, scrapes off failed coating and loose material, and repairs soft or damaged
              substrate. Foam only bonds to a clean, dry, sound surface, so this step decides
              whether the roof lasts 25 years or 5.
            </li>
            <li>
              <strong>Everything that isn't roof gets masked.</strong> Walls, windows, AC units,
              vents, solar, skylights, cars, and plants. Overspray is the most common complaint
              homeowners have about foam crews, and it's entirely preventable with masking.
            </li>
            <li>
              <strong>The foam goes on as a liquid and rises in seconds.</strong> Two chemicals
              meet at the spray gun and expand roughly thirty times in volume. It looks like warm
              tan-colored cake frosting and is walkable within minutes. We build it in passes
              rather than one heavy lift, and spray it thicker in low spots so water actually runs
              to the drains instead of sitting.
            </li>
            <li>
              <strong>Then the coating — the part that makes it last.</strong> Bare foam degrades
              in UV, so a two-coat reflective elastomeric system goes over it at a specified mil
              thickness, with granules embedded in walk areas. This bright white surface is what
              reflects the heat and what gets renewed at recoat time.
            </li>
            <li>
              <strong>Final walk and cleanup.</strong> Coating thickness is checked, penetrations
              are inspected, masking comes off, and the owner walks the roof with us before final
              payment.
            </li>
          </ol>
          <p>
            You'll hear the spray rig running and there is a noticeable chemical smell during the
            foam phase — it clears within hours, and we tell you ahead of time which windows and
            AC intakes to keep closed while we spray. See{" "}
            <Link to="/blog/$slug" params={{ slug: "what-is-foam-roof-phoenix" }} className={A}>
              what a foam roof actually is
            </Link>{" "}
            for the background, or{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>book a free roof inspection</Link>{" "}
            to find out whether your deck is a candidate.
          </p>
        </div>
      ),
    },
    {
      heading: "Foam roofing vs. tile, shingle, TPO, and built-up in Phoenix",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            This is the question almost everyone asks next. Here's the honest comparison for a
            low-slope Phoenix roof — including where foam isn't the answer.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Foam (SPF):</strong> 25+ years with recoats, R-6.5 per inch, no seams or
              fasteners to leak, reflects 80–85% of the sun, and can be re-sloped to kill ponding.
              Needs a coating renewal roughly every 5 years and needs a crew that actually
              specializes in it.
            </li>
            <li>
              <strong>Tile:</strong> The tile lasts decades, but tile isn't what keeps water out —
              the underlayment beneath it is, and that's what bakes out in Arizona. Great on steep
              slopes and for curb appeal; it can't be used on the flat sections most Phoenix homes
              also have. See{" "}
              <Link to="/tile-roofing" className={A}>tile roofing</Link> and{" "}
              <Link to="/tile-roof-repair-phoenix" className={A}>tile roof repair</Link>.
            </li>
            <li>
              <strong>Shingle:</strong> Cheapest up front and fine on pitched roofs, but Phoenix
              UV and heat shorten its life compared to cooler climates, it adds no meaningful
              insulation, and it can't go on a flat roof at all.
            </li>
            <li>
              <strong>TPO and other single-ply:</strong> A good system when installed well, but
              every seam and every penetration is a hand-detailed failure point, and end of life
              means a full tear-off into a dumpster.
            </li>
            <li>
              <strong>Built-up (tar and gravel):</strong> The old Phoenix standard. Heavy, no real
              insulation value, hard to find leaks in, and it can't be re-sloped. Most of the
              built-up roofs we touch get converted to foam instead of rebuilt.
            </li>
          </ul>
          <p>
            The short version: on a flat or low-slope Phoenix roof, foam wins on insulation, leak
            resistance, and long-run cost. On a steep pitch you're choosing between tile and
            shingle. Plenty of Valley homes have both, and we spec them together. Compare the
            numbers in our{" "}
            <Link to="/foam-roof-replacement-cost-phoenix" className={A}>foam roof replacement cost guide</Link>{" "}
            or read{" "}
            <Link to="/blog/$slug" params={{ slug: "best-flat-roof-phoenix-homes" }} className={A}>
              foam vs. TPO vs. built-up for Phoenix homes
            </Link>.
          </p>
        </div>
      ),
    },
    {
      heading: "The downsides of a foam roof (yes, there are some)",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            We install foam on most of the homes we re-roof, and we still tell people the
            trade-offs up front. If a contractor won't, that's the warning sign.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>It isn't maintenance-free.</strong> The coating is a wear layer. Plan on a
              recoat roughly every 5 years in Phoenix sun. Skipping recoats is the number one
              reason foam roofs fail early — see{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>foam roof coating and recoating</Link>.
            </li>
            <li>
              <strong>Installation quality matters more than with other roofs.</strong> Foam is
              mixed and applied on site, so temperature, humidity, ratio, and prep all affect the
              result. A bad foam install is genuinely bad. Ask how long the company has sprayed
              foam and who inspects the finished job.
            </li>
            <li>
              <strong>It dents and cuts.</strong> Dropped tools, dragged ladders, and heavy foot
              traffic can gouge the surface. We embed granules in walk paths and around rooftop
              equipment, and any puncture needs sealing promptly instead of at the next recoat.
            </li>
            <li>
              <strong>Repairs need a foam person.</strong> Any roofer can slap mastic on it; far
              fewer can repair it correctly. Keep a foam specialist's number handy —{" "}
              <Link to="/foam-roof-repair-phoenix" className={A}>foam roof repair in Phoenix</Link>.
            </li>
            <li>
              <strong>Smell and overspray during install.</strong> Temporary, but real. Proper
              masking and a heads-up to neighbors handles it.
            </li>
          </ul>
          <p>
            Weighing it out? Read our{" "}
            <Link to="/blog/$slug" params={{ slug: "foam-roof-pros-and-cons" }} className={A}>
              honest foam roof pros and cons
            </Link>{" "}
            and{" "}
            <Link to="/blog/$slug" params={{ slug: "how-long-does-a-foam-roof-last-in-phoenix" }} className={A}>
              how long a foam roof actually lasts in Phoenix
            </Link>.
          </p>
        </div>
      ),
    },
  ],


  "foam-roof-repair-phoenix": [
    {
      heading: "Foam roof repair in Phoenix: what it costs and how fast it happens",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            <strong>Foam roof repair</strong> in Phoenix means cutting out the failed section of
            spray polyurethane foam, refoaming it to match the surrounding thickness, and rebuilding
            the elastomeric coating over the patch so the roof stays seamless. Most single-source
            repairs are finished in a half-day to a day, and most Phoenix homeowners spend far less
            on a repair than on any form of replacement.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Small patch (bird pecks, a blister, one penetration):</strong> a same-visit
              repair in most cases.
            </li>
            <li>
              <strong>Section repair (wet foam cut out and replaced):</strong> one day, plus coating
              cure time.
            </li>
            <li>
              <strong>Coating-only failure:</strong> not a repair at all — it&apos;s an overdue{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>foam roof recoat</Link>, and it
              costs a fraction of foam replacement.
            </li>
            <li>
              <strong>Current price ranges:</strong>{" "}
              <Link to="/roof-repair-cost-phoenix" className={A}>Phoenix roof repair costs</Link>{" "}
              and{" "}
              <Link to="/foam-roof-recoating-cost-in-phoenix" className={A}>foam recoating costs</Link>.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Foam roof repair questions Phoenix homeowners ask us most",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>&ldquo;Can a foam roof be repaired, or do I need a new one?&rdquo;</strong> Most
              can be repaired. Because SPF is seamless, we cut out only the wet section, replace the
              foam, and blend new coating over it. Full{" "}
              <Link to="/foam-roofing" className={A}>foam roof replacement</Link> is only necessary
              when saturation is widespread.
            </li>
            <li>
              <strong>&ldquo;Why is my foam roof bubbling or blistering?&rdquo;</strong> Blisters are
              usually trapped moisture or a coating failure. Some are cosmetic, some mean water is
              already under the surface — read{" "}
              <Link to="/blog/$slug" params={{ slug: "foam-roof-bubbles-causes-repair-phoenix" }} className={A}>
                what foam roof bubbles mean in Phoenix
              </Link>.
            </li>
            <li>
              <strong>&ldquo;Is this a repair or just an overdue recoat?&rdquo;</strong> If the foam
              is dry and the coating is chalky and thin, you need a{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>recoat</Link>, not a repair —
              and it costs far less.
            </li>
            <li>
              <strong>&ldquo;Do you repair SPF roofs another company installed?&rdquo;</strong> Yes.
              We repair spray foam and polyurethane foam roofs regardless of who installed them,
              including roofs whose original installer is out of business.
            </li>
            <li>
              <strong>&ldquo;My whole roof needs work — what then?&rdquo;</strong> See{" "}
              <Link to="/foam-roofing" className={A}>spray foam roofing</Link> for full systems, or{" "}
              <Link to="/roof-replacement-phoenix" className={A}>roof replacement in Phoenix</Link>{" "}
              if other roof types are involved too.
            </li>
          </ul>
          <p>
            Not sure which of these you&apos;re dealing with? A{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>free roof inspection</Link>{" "}
            answers it with photos before you spend anything.
          </p>
        </div>
      ),
    },
  ],


  "commercial-foam-roofing": [
    {
      heading: "Commercial foam roofing, SPF re-roofing, and roof restoration — which do you need?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Facility managers search for three different things and often mean the same building
            problem. Here's how we sort it out on the first walk:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Commercial foam roofing / SPF re-roofing:</strong> new spray polyurethane foam
              installed over a sound existing deck — no tear-off, no tenant shutdown, added R-value
              and positive drainage built into the foam.
            </li>
            <li>
              <strong>Roof restoration / roof coating:</strong> the existing roof still has life, so
              we clean, repair, and recoat it. See{" "}
              <Link to="/commercial-roof-coating" className={A}>commercial roof coating</Link>.
            </li>
            <li>
              <strong>Commercial roof repair:</strong> targeted leak work on penetrations, seams,
              parapets, and HVAC curbs —{" "}
              <Link to="/commercial-roof-repair" className={A}>commercial roof repair</Link>.
            </li>
          </ul>
          <p>
            Budgeting first? Our{" "}
            <Link to="/commercial-roofing-cost-phoenix" className={A}>commercial roofing cost guide for Phoenix</Link>{" "}
            gives per-square-foot ranges by system before you request bids.
          </p>
        </div>
      ),
    },
    {
      heading: "What a commercial foam roof costs to own over 30 years",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Install price is the wrong number to compare. What matters to an owner or a board is
            what the roof costs over the life of the building, and that's where foam separates
            itself from single-ply.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Foam, 30 years:</strong> one install, then a recoat about every 5 years. The
              foam substrate underneath never gets thrown away, so you're only ever re-buying the
              coating — a fraction of a replacement — and the roof stays under warranty the whole
              time.
            </li>
            <li>
              <strong>Single-ply, 30 years:</strong> one install, then a tear-off and full
              replacement somewhere around year 15–20, and often a second one before year 30.
              Each cycle means disposal fees, dumpsters, tenant disruption, and a roof exposed to
              weather mid-project.
            </li>
            <li>
              <strong>What else moves the number:</strong> energy. R-6.5 per inch plus a reflective
              white surface takes real load off rooftop HVAC in a market where it runs eight-plus
              months a year, and a properly coated SPF roof qualifies for most Phoenix-metro
              utility cool-roof rebate programs.
            </li>
          </ul>
          <p>
            Every building is different — deck condition, drain layout, HVAC density, and access
            all move the price — so we put per-square-foot ranges in writing rather than quoting
            from a satellite photo. Start with our{" "}
            <Link to="/commercial-roofing-cost-phoenix" className={A}>commercial roofing cost guide for Phoenix</Link>{" "}
            and then{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>request a free commercial roof inspection</Link>{" "}
            for real numbers on your roof.
          </p>
        </div>
      ),
    },
    {
      heading: "What a foam-over reroof looks like on a building that stays open",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            The most common question we get from facility managers isn't about foam. It's
            &ldquo;can we keep operating?&rdquo; Almost always, yes — here's how we run it.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>No tear-off means no open roof.</strong> On a foam-over we clean and prep the
              existing membrane and spray directly over it. Your deck is never exposed, so a
              surprise storm doesn't become an interior loss.
            </li>
            <li>
              <strong>Staging is planned around your traffic.</strong> Rig placement, hose runs,
              and material drops get mapped before day one so loading docks, drive lanes, and
              customer entrances stay clear.
            </li>
            <li>
              <strong>Odor phases are scheduled and announced.</strong> The spray phase has a
              noticeable smell. We tell you which days and which roof areas, and which rooftop
              air intakes to shut down or temporarily filter while we work above them.
            </li>
            <li>
              <strong>Tenant notice in writing.</strong> We give you the notice language and the
              schedule to distribute, so tenants hear it from you before they hear the rig.
            </li>
            <li>
              <strong>Phased sections.</strong> Retail centers and multi-tenant buildings get
              sprayed in sections, each one coated and watertight before we move on.
            </li>
            <li>
              <strong>Daily updates.</strong> Your facilities contact gets progress and photos
              each day, not a phone call at the end.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Maintenance and recoat programs for commercial foam roofs",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            A foam roof is only renewable if somebody actually renews it. Our maintenance program
            exists so the recoat happens on schedule instead of after a leak.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Annual inspection with photo documentation.</strong> Coating condition,
              penetrations, drains and scuppers, parapets, HVAC curbs, and any new damage from
              trades working on the roof.
            </li>
            <li>
              <strong>Scheduled recoats.</strong> Roughly every 5 years in Phoenix sun, planned
              into your budget cycle rather than sprung on you — see{" "}
              <Link to="/commercial-roof-coating" className={A}>commercial roof coating</Link>.
            </li>
            <li>
              <strong>Minor repairs handled during the visit.</strong> Punctures from foot traffic
              and equipment work get sealed while they're still cheap —{" "}
              <Link to="/commercial-roof-repair" className={A}>commercial roof repair</Link>.
            </li>
            <li>
              <strong>Documentation you can hand up.</strong> A written report with photos for
              ownership, an HOA board, a lender, or a buyer during due diligence.
            </li>
            <li>
              <strong>Warranty kept in force.</strong> Manufacturer warranties require
              maintenance. The program is what keeps yours valid for its full term.
            </li>
          </ul>
          <p>
            Managing an association or multifamily property? See{" "}
            <Link to="/hoa-roofing" className={A}>HOA roofing</Link> and{" "}
            <Link to="/apartment-roofing" className={A}>apartment and condo roofing</Link>.
          </p>
        </div>
      ),
    },
  ],


  "commercial-roofing": [
    {
      heading: "Looking for a commercial roofing company in Phoenix? Start here",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Whether you searched for a <strong>commercial roofing contractor in Phoenix</strong>, a{" "}
            <strong>flat roof company</strong>, or <strong>commercial roof replacement</strong>, these
            are the paths our commercial clients take:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Active leak, tenant complaint, or storm event:</strong>{" "}
              <Link to="/commercial-roof-repair" className={A}>commercial roof repair</Link> or{" "}
              <Link to="/storm-damage-repair" className={A}>storm damage repair</Link>.
            </li>
            <li>
              <strong>Roof at end of life:</strong>{" "}
              <Link to="/commercial-foam-roofing" className={A}>commercial foam roofing</Link> — the
              lowest-disruption re-roof for Phoenix low-slope buildings.
            </li>
            <li>
              <strong>Cutting cooling costs / extending roof life:</strong>{" "}
              <Link to="/commercial-roof-coating" className={A}>commercial roof coating and restoration</Link>.
            </li>
            <li>
              <strong>Multi-building portfolios and associations:</strong>{" "}
              <Link to="/hoa-roofing" className={A}>HOA and property management roofing</Link>,{" "}
              <Link to="/apartment-roofing" className={A}>apartment roofing</Link>, and{" "}
              <Link to="/warehouse-roofing" className={A}>warehouse roofing</Link>.
            </li>
            <li>
              <strong>Budget and capital planning:</strong>{" "}
              <Link to="/commercial-roofing-cost-phoenix" className={A}>commercial roofing costs in Phoenix</Link>.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Built-up, TPO, and mod-bit roofs: re-roof, restore, or replace?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Most Phoenix commercial buildings we&apos;re called to have one of three systems on
            them: <strong>built-up roofing (BUR / hot-mop tar and gravel)</strong>, single-ply{" "}
            <strong>TPO</strong>, or <strong>modified bitumen</strong>. We inspect, repair, and
            recoat all three. What we recommend next depends on what the core samples and the
            drainage tell us — not on what we&apos;d prefer to install.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Built-up roofing in Phoenix:</strong> decades of Valley office and retail
              buildings sit on tar and gravel. When the substrate is dry, a coating restoration
              extends it. When it&apos;s saturated, foam over a prepared deck avoids a full tear-off
              — see{" "}
              <Link to="/commercial-foam-roofing" className={A}>commercial foam roofing</Link>.
            </li>
            <li>
              <strong>Commercial re-roof vs. full replacement:</strong> a re-roof goes over a sound
              existing deck with no tear-off and no tenant shutdown. Replacement means removal and
              disposal first, and it&apos;s only warranted when the deck or insulation is wet.
            </li>
            <li>
              <strong>Ponding water:</strong> membranes can&apos;t fix slope. Foam can be built up
              to move water to the drains, which is why it&apos;s our standard answer for flat
              Phoenix roofs that hold water after a monsoon.
            </li>
            <li>
              <strong>Restoration instead of capital spend:</strong>{" "}
              <Link to="/commercial-roof-coating" className={A}>commercial roof coating</Link> can
              add years of service life at a fraction of replacement cost, on a maintenance budget
              rather than a capital one.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Commercial roofing across Phoenix, Scottsdale, and the wider Valley",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            We&apos;re an Arizona commercial roofing contractor licensed under ROC223900 and
            family-owned since 1989. Our commercial crews work Phoenix and the whole metro —
            Scottsdale and Old Town, Tempe, Mesa, Chandler, Gilbert, Glendale, Peoria, Surprise,
            Carefree, and Cave Creek — plus outlying Arizona work in Prescott, Casa Grande, and
            Tucson. Full list on our{" "}
            <Link to="/service-areas" className={A}>service areas page</Link>.
          </p>
          <p>
            Building types we cover: {""}
            <Link to="/warehouse-roofing" className={A}>warehouses and industrial buildings</Link>,{" "}
            <Link to="/apartment-roofing" className={A}>apartment and multifamily properties</Link>,{" "}
            <Link to="/hoa-roofing" className={A}>HOA and association portfolios</Link>, retail
            centers, offices, churches, and schools. Need numbers before bids?{" "}
            <Link to="/commercial-roofing-cost-phoenix" className={A}>Commercial roofing costs in Phoenix</Link>{" "}
            gives per-square-foot ranges by system.
          </p>
        </div>
      ),
    },
  ],


  "roof-leak-repair": [
    {
      heading: "How much does roof leak repair cost in Phoenix, and how fast can it be fixed?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Most Phoenix roof leaks come from a single failure point — a cracked pipe boot, a
            slipped tile, an opened wall flashing, a worn foam coating, or a skylight curb. Once the
            source is found, the majority of leaks are repaired in a half-day to a day, and the
            repair costs a small fraction of a roof replacement. Multi-point failures or repairs
            needing dry time run two to three days.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Diagnosis:</strong> free, with photos of the actual failure on the roof.
            </li>
            <li>
              <strong>Typical repair pricing:</strong> see{" "}
              <Link to="/roof-repair-cost-phoenix" className={A}>Phoenix roof repair cost ranges</Link>{" "}
              by roof type and failure.
            </li>
            <li>
              <strong>Water coming in right now:</strong>{" "}
              <Link to="/emergency-roof-leak-repair" className={A}>emergency roof leak repair</Link>{" "}
              — call 480-941-5373 and we respond right away.
            </li>
            <li>
              <strong>Insurance:</strong> storm-driven damage is often covered; gradual wear
              generally isn&apos;t. We document conditions either way, and{" "}
              <Link to="/storm-damage-repair" className={A}>storm damage repair</Link> covers the
              post-monsoon side.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Roof leak repair in Phoenix: what to search for, and what to do first",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            People find this page searching <strong>roof leak repair Phoenix</strong>,{" "}
            <strong>emergency roof leak repair</strong>, and{" "}
            <strong>how to stop a roof leak</strong>. The right next step depends on whether water is
            moving right now:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Water actively coming in:</strong> call us and see{" "}
              <Link to="/emergency-roof-leak-repair" className={A}>emergency roof leak repair</Link> —
              we respond right away, including during monsoon storms.
            </li>
            <li>
              <strong>Stain on the ceiling, no active drip:</strong> it's still a leak path. Read{" "}
              <Link to="/how-to-fix-a-leaking-roof-from-inside" className={A}>how to handle a leaking roof from inside</Link>{" "}
              while you wait for the inspection.
            </li>
            <li>
              <strong>Leak after a storm:</strong>{" "}
              <Link to="/storm-damage-repair" className={A}>storm damage roof repair</Link>.
            </li>
            <li>
              <strong>Leak around a skylight:</strong>{" "}
              <Link to="/skylight-repair" className={A}>skylight repair</Link> — one of the most
              common leak sources in the Valley.
            </li>
            <li>
              <strong>Leak on a flat or foam roof:</strong>{" "}
              <Link to="/flat-roof-repair" className={A}>flat roof repair</Link> or{" "}
              <Link to="/foam-roof-repair-phoenix" className={A}>foam roof repair</Link>.
            </li>
            <li>
              <strong>Wondering what it costs:</strong>{" "}
              <Link to="/roof-repair-cost-phoenix" className={A}>Phoenix roof repair costs</Link>.
            </li>
          </ul>
        </div>
      ),
    },
  ],


  "roof-repair": [
    {
      heading: "Roof repair in Phoenix by roof type",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            &ldquo;<strong>Roof repair Phoenix</strong>&rdquo; covers very different work depending on
            what's over your head. Jump straight to yours:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Flat or low-slope roof:</strong>{" "}
              <Link to="/flat-roof-repair" className={A}>flat roof repair</Link>.
            </li>
            <li>
              <strong>Foam roof:</strong>{" "}
              <Link to="/foam-roof-repair-phoenix" className={A}>foam roof repair</Link>, or a{" "}
              <Link to="/foam-roof-coating-recoating" className={A}>recoat</Link> if the coating is
              simply worn out.
            </li>
            <li>
              <strong>Tile roof:</strong>{" "}
              <Link to="/tile-roofing" className={A}>tile roofing and underlayment work</Link>.
            </li>
            <li>
              <strong>Shingle roof:</strong>{" "}
              <Link to="/shingle-roofing" className={A}>shingle roofing</Link>.
            </li>
            <li>
              <strong>Leak or storm damage:</strong>{" "}
              <Link to="/roof-leak-repair" className={A}>roof leak repair</Link> and{" "}
              <Link to="/storm-damage-repair" className={A}>storm damage repair</Link>.
            </li>
          </ul>
          <p>
            Not sure whether to repair or replace? A{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>free roof inspection</Link> settles
            it with photos, and{" "}
            <Link to="/roof-repair-cost-phoenix" className={A}>our cost guide</Link> shows the
            typical ranges either way.
          </p>
        </div>
      ),
    },
  ],

  "foam-roof-coating-recoating": [
    {
      heading: "Roof coating in Phoenix: elastomeric, silicone, and how often to recoat",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Roof coating Phoenix:</strong> a reflective liquid membrane rolled or sprayed
              over a sound roof. On foam it's not optional — the coating is what protects the foam
              from UV.
            </li>
            <li>
              <strong>Elastomeric roof coating:</strong> the acrylic system we use on most Phoenix
              foam roofs, applied in two coats at manufacturer mil thickness.
            </li>
            <li>
              <strong>Silicone roof coating:</strong> better at standing water, different
              maintenance tradeoffs — compare them in{" "}
              <Link to="/blog/$slug" params={{ slug: "silicone-vs-elastomeric-roof-coatings-for-foam-roofs-in-phoenix" }} className={A}>
                silicone vs. elastomeric coatings for foam roofs
              </Link>.
            </li>
            <li>
              <strong>How often to recoat in Phoenix:</strong> about every 5 years. Missing recoats
              is the single most common reason a Valley foam roof fails early.
            </li>
            <li>
              <strong>Roof coating cost:</strong> see{" "}
              <Link to="/foam-roof-recoating-cost-in-phoenix" className={A}>foam roof recoating costs in Phoenix</Link>.
            </li>
          </ul>
          <p>
            Commercial building? The same restoration approach scales — see{" "}
            <Link to="/commercial-roof-coating" className={A}>commercial roof coating</Link>.
          </p>
        </div>
      ),
    },
    {
      heading: "Is roof coating the right answer, or do you need a new roof?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            <strong>Roof coating in Phoenix</strong> works when the roof underneath is structurally
            sound and dry. It is not a fix for a wet roof — coating over saturated foam or soaked
            insulation traps the water and buys you nothing. Here&apos;s how we sort it on the
            inspection:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Coating is the right call:</strong> the surface is chalky, thin, or faded, the
              foam or membrane below is dry, and there are no active leaks. Expect roughly
              $1.00–$3.00 per square foot on a typical Phoenix home.
            </li>
            <li>
              <strong>Repair first, then coat:</strong> isolated blisters, bird pecks, or open
              penetrations get fixed before any coating goes on —{" "}
              <Link to="/foam-roof-repair-phoenix" className={A}>foam roof repair</Link>.
            </li>
            <li>
              <strong>Replacement is the cheaper answer:</strong> widespread wet foam or failed
              substrate. See{" "}
              <Link to="/foam-roofing" className={A}>spray foam roofing</Link> or{" "}
              <Link to="/roof-replacement-phoenix" className={A}>roof replacement in Phoenix</Link>.
            </li>
            <li>
              <strong>Best time to coat in Phoenix:</strong> spring before monsoon, or fall once
              surface temperatures drop. Coatings need dry weather and a surface within the
              manufacturer&apos;s temperature window to cure properly.
            </li>
          </ul>
          <p>
            A <Link to="/free-roof-inspection-phoenix" className={A}>free roof inspection</Link>{" "}
            tells you which of those four you&apos;re in, with photos, before you spend anything.
          </p>
        </div>
      ),
    },
  ],


  "skylight-replacement-phoenix": [
    {
      heading: "Skylight replacement, skylight repair, or a new solar skylight?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Phoenix homeowners search all of these when a skylight goes bad. Here&rsquo;s what each one
            actually means.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Skylight replacement:</strong> the unit itself is done — fogged glass, a yellowed
              or cracked dome, a rotted curb, or repeat leaks. This page.
            </li>
            <li>
              <strong>Skylight repair / skylight leak repair:</strong> the glazing is fine and the
              flashing, gasket, or sealant failed. Cheaper and far more common —{" "}
              <Link to="/skylight-repair" className={A}>skylight repair in Phoenix</Link>.
            </li>
            <li>
              <strong>Skylight replacement cost:</strong> ranges and what drives them —{" "}
              <Link to="/skylight-repair-cost-phoenix" className={A}>skylight costs in Phoenix</Link>.
            </li>
            <li>
              <strong>Solar skylights, solar tubes, sun tunnels:</strong> Velux solar-powered units with
              remote-controlled shades, or a reflective tube for hallways and baths. Both covered above.
            </li>
            <li>
              <strong>Roof leaking near a skylight:</strong> often the roof, not the skylight —{" "}
              <Link to="/roof-leak-repair" className={A}>roof leak repair</Link> starts with the
              diagnosis.
            </li>
          </ul>
          <p>
            Whichever it turns out to be, the first step is the same and free: a{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>roof inspection with photos</Link>.
            We serve Phoenix, Scottsdale, Paradise Valley, Mesa, Chandler, Tempe, Gilbert, Glendale,
            Peoria, Cave Creek, Carefree, Fountain Hills and the surrounding Valley.
          </p>
        </div>
      ),
    },
  ],

  "tile-roof-repair-phoenix": [
    {
      heading: "Tile roof repair, tile replacement, or a tile roofing company near you?",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            People search all of these for the same roof, and they mean different jobs. Here's the
            plain-English version so you know which one you actually need.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Tile roof repair:</strong> broken or slipped tile, a leaking valley or
              flashing, cracked ridge mortar, or a section of failed underlayment. Same-day to
              two-day work — this page.
            </li>
            <li>
              <strong>Tile roof replacement / tile R&amp;R:</strong> the underlayment across the
              whole roof is at end of life. Your tile is removed, new underlayment is installed, and
              the same tile goes back on. See{" "}
              <Link to="/tile-roof-replacement-cost-in-phoenix" className={A}>tile roof replacement cost in Phoenix</Link>.
            </li>
            <li>
              <strong>Tile roofing company / tile roofing contractor:</strong> the full service —
              installs, re-roofs, and repairs. That's our{" "}
              <Link to="/tile-roofing" className={A}>tile roofing page</Link>.
            </li>
            <li>
              <strong>Roof leak repair:</strong> if you know you're leaking but not why, start at{" "}
              <Link to="/roof-leak-repair" className={A}>roof leak repair in Phoenix</Link>. We
              diagnose first and only pull tile if the tile is the problem.
            </li>
            <li>
              <strong>Emergency tile repair:</strong> active water coming in during a storm —{" "}
              <Link to="/emergency-roof-leak-repair" className={A}>emergency roof leak repair</Link>.
            </li>
          </ul>
        </div>
      ),
    },
    {
      heading: "Tile roof repair across the Valley",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            We repair tile roofs throughout Maricopa County — Phoenix, Scottsdale, Paradise Valley,
            Mesa, Chandler, Tempe, Gilbert, Glendale, Peoria, Cave Creek, Carefree, Fountain Hills,
            Ahwatukee, and the surrounding communities. Tile is the dominant roof in most of these
            neighborhoods, which means we're on tile nearly every day of the week.
          </p>
          <p>
            Whatever the roof turns out to need, the first step is the same and it's free: a{" "}
            <Link to="/free-roof-inspection-phoenix" className={A}>roof inspection with photos</Link>{" "}
            and a written estimate. If the roof is fine, we'll say so.
          </p>
        </div>
      ),
    },
  ],

  "flat-roof-repair": [
    {
      heading: "Flat roof repair in Phoenix: common searches, straight answers",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>&ldquo;Flat roof repair near me&rdquo;:</strong> we repair low-slope roofs
              across Phoenix, Scottsdale, Mesa, and the wider Valley — see{" "}
              <Link to="/service-areas" className={A}>our service areas</Link>.
            </li>
            <li>
              <strong>&ldquo;Water pooling on flat roof&rdquo;:</strong> ponding means the slope is
              wrong, not just that the surface leaks. Foam is the one system we can build up to
              correct drainage — see{" "}
              <Link to="/foam-roofing" className={A}>foam roofing</Link>.
            </li>
            <li>
              <strong>&ldquo;Flat roof leak repair&rdquo;:</strong> start at{" "}
              <Link to="/roof-leak-repair" className={A}>roof leak repair</Link>, or{" "}
              <Link to="/emergency-roof-leak-repair" className={A}>emergency leak repair</Link> if
              water is coming in now.
            </li>
            <li>
              <strong>&ldquo;Best flat roof material for Arizona&rdquo;:</strong> seamless spray foam,
              for heat, monsoon rain, and thermal movement — the reasoning is on our{" "}
              <Link to="/residential-foam-roof" className={A}>residential foam roof page</Link>.
            </li>
            <li>
              <strong>&ldquo;Flat roof repair cost&rdquo;:</strong>{" "}
              <Link to="/flat-roof-repair-cost-phoenix" className={A}>flat roof repair costs in Phoenix</Link>.
            </li>
          </ul>
        </div>
      ),
    },
  ],

  "roof-replacement-phoenix": [
    {
      heading: "Roof replacement in Phoenix: choosing between foam, tile, and shingle",
      body: (
        <div className="space-y-4 text-[16px] leading-relaxed text-foreground/85">
          <p>
            Searches like <strong>roof replacement Phoenix</strong>,{" "}
            <strong>new roof cost Phoenix</strong>, and <strong>re-roofing near me</strong> all come
            down to one decision: which system goes back on, and over what.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Flat or low-slope sections:</strong>{" "}
              <Link to="/foam-roofing" className={A}>spray foam roofing</Link> — seamless, insulating,
              and often installed without a full tear-off.
            </li>
            <li>
              <strong>Tile roof:</strong> the tile usually gets reused; the{" "}
              <Link to="/tile-roofing" className={A}>underlayment is what's failing</Link>.
            </li>
            <li>
              <strong>Shingle roof:</strong>{" "}
              <Link to="/shingle-roofing" className={A}>shingle replacement</Link> and{" "}
              <Link to="/shingle-roof-cost-phoenix" className={A}>shingle roof costs</Link>.
            </li>
            <li>
              <strong>Repair vs. replace:</strong> get a photo-documented{" "}
              <Link to="/free-roof-inspection-phoenix" className={A}>free roof inspection</Link>{" "}
              before you commit to a full replacement.
            </li>
            <li>
              <strong>Paying for it:</strong>{" "}
              <Link to="/financing" className={A}>cost solutions</Link>.
            </li>
          </ul>
        </div>
      ),
    },
  ],
};
